import Centrifuge from "centrifuge";

let centrifugeSingleton: Centrifuge | null = null;
let listenersAttached = false;

const subscriptionsRegistry = new Map<string, {
  subscription: Centrifuge.Subscription;
  refCount: number;
}>();

type CentrifugoStatus = "connected" | "disconnected" | "error";
type CentrifugeWithNewSubscription = Centrifuge & {
  newSubscription?: (channel: string) => Centrifuge.Subscription;
};

function normalizePublicUrl(rawValue: string | undefined, envName: string): string | undefined {
  if (!rawValue) return undefined;

  const trimmed = rawValue.trim();
  const normalized = trimmed.replace(/[§"'`]+$/gu, "");

  if (process.env.NODE_ENV !== "production" && normalized !== trimmed) {
    console.warn(`[Centrifugo] Normalized ${envName}`, {
      from: trimmed,
      to: normalized,
    });
  }

  return normalized;
}

function emitCentrifugoStatus(status: CentrifugoStatus, details?: Record<string, unknown>): void {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent("centrifugo-status-changed", {
      detail: {
        status,
        ...details,
      },
    }),
  );
}

function removeSubscription(subscription: Centrifuge.Subscription): void {
  const removable = subscription as Centrifuge.Subscription & {
    remove?: () => void;
  };
  removable.remove?.();
}

export function getCentrifuge(): Centrifuge | null {
  if (typeof window === "undefined") return null;

  if (centrifugeSingleton) return centrifugeSingleton;

  const url = normalizePublicUrl(
    process.env.NEXT_PUBLIC_CENTRIFUGO_HOST,
    "NEXT_PUBLIC_CENTRIFUGO_HOST",
  );
  if (!process.env.NEXT_PUBLIC_CENTRIFUGO_HOST) {
    console.warn(
      "NEXT_PUBLIC_CENTRIFUGO_HOST is not set; falling back to DEFAULT_CENTRIFUGE_URL"
    );
  }

  const subscribeEndpoint = normalizePublicUrl(
    process.env.NEXT_PUBLIC_LARAVEL_SUBSCRIBE_ENDPOINT,
    "NEXT_PUBLIC_LARAVEL_SUBSCRIBE_ENDPOINT",
  );
  if (!subscribeEndpoint) {
    console.warn(
      "NEXT_PUBLIC_LARAVEL_SUBSCRIBE_ENDPOINT is not set; private channel subscriptions may fail"
    );
  }

  if (!url) {
    console.error("[Centrifugo] Missing WS URL, cannot initialize Centrifuge client");
    return null;
  }

  if (process.env.NODE_ENV !== "production") {
    console.info("[Centrifugo] Resolved subscribe endpoint:", subscribeEndpoint ?? "(none)");
  }

  centrifugeSingleton = new Centrifuge(url, {
    subscribeEndpoint,
  });

  // Log successful WS connect (once).
  if (!listenersAttached) {
    listenersAttached = true;

    centrifugeSingleton.on("connected", (ctx: unknown) => {
      emitCentrifugoStatus("connected", { raw: ctx });
    });

    centrifugeSingleton.on("disconnected", (ctx: unknown) => {
      emitCentrifugoStatus("disconnected", { raw: ctx });
    });

    centrifugeSingleton.on("error", (ctx: unknown) => {
      const record = ctx && typeof ctx === "object" ? (ctx as Record<string, unknown>) : null;
      console.error("[Centrifugo] error", ctx);
      emitCentrifugoStatus("error", {
        message:
          typeof record?.message === "string" && record.message.trim()
            ? record.message
            : "Centrifugo connection error",
        raw: ctx,
      });
    });
  }

  return centrifugeSingleton;
}

/**
 * Увеличить счетчик ссылок для канала
 * @returns { subscription, isNew } - подписка и флаг, новая ли она
 */
export function acquireSubscription(channel: string): {
  subscription: Centrifuge.Subscription;
  isNew: boolean;
} | null {
  const centrifuge = getCentrifuge();
  if (!centrifuge) return null;

  const entry = subscriptionsRegistry.get(channel);

  if (entry) {
    // Увеличиваем счетчик ссылок
    entry.refCount++;
    return {
      subscription: entry.subscription,
      isNew: false
    };
  }

  const typedCentrifuge = centrifuge as CentrifugeWithNewSubscription;
  const subscription =
    typedCentrifuge.newSubscription?.(channel) ?? centrifuge.subscribe(channel, {});

  subscriptionsRegistry.set(channel, {
    subscription,
    refCount: 1,
  });

  return {
    subscription,
    isNew: true
  };
}

/**
 * Уменьшить счетчик ссылок, удалить подписку если счетчик = 0
 */
export function releaseSubscription(channel: string): void {
  const entry = subscriptionsRegistry.get(channel);
  if (!entry) return;

  entry.refCount--;

  if (entry.refCount <= 0) {
    try {
      entry.subscription.unsubscribe();
    } catch (e) {
      console.error("[Centrifugo] Error unsubscribing:", e);
    }

    try {
      removeSubscription(entry.subscription);
    } catch (e) {
      console.error("[Centrifugo] Error removing subscription:", e);
    }

    subscriptionsRegistry.delete(channel);
  }
}
